import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {ArrowRight, Check, Copy, Linkedin, Twitter, Facebook, Sparkles, Target, Compass, RefreshCw, Download, Image as ImageIcon, Loader2} from 'lucide-react'
import { toPng } from 'html-to-image'
import Nav from '@/components/Nav'
import Footer from '@/components/Footer'
import ScoreCard from '@/components/ScoreCard'
import { questions, maxScore } from '@/data/questions'
import { getCategoryScores, getTier } from '@/lib/scoring'
import { clearAll, loadAnswers, loadLead } from '@/lib/store'

function Results() {
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloaded, setDownloaded] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const answers = useMemo(() => loadAnswers(), [])
  const lead = useMemo(() => loadLead(), [])

  useEffect(() => {
    if (!lead || Object.keys(answers).length < questions.length) {
      navigate('/quiz')
    }
  }, [answers, lead, navigate])

  const totalScore = useMemo(
    () => Object.values(answers).reduce((sum, v) => sum + v, 0),
    [answers]
  )
  const tier = useMemo(() => getTier(totalScore), [totalScore])
  const categories = useMemo(() => getCategoryScores(answers, questions), [answers])
  const percent = Math.round((totalScore / maxScore) * 100)

  const tierColors: Record<string, { bg: string; text: string; ring: string }> = {
    emerging: { bg: 'bg-[#F59E0B]', text: 'text-[#F59E0B]', ring: 'ring-[#F59E0B]/30' },
    developing: { bg: 'bg-[#0F766E]', text: 'text-[#0F766E]', ring: 'ring-[#0F766E]/30' },
    established: { bg: 'bg-[#0F766E]', text: 'text-[#0F766E]', ring: 'ring-[#0F766E]/30' },
    leading: { bg: 'bg-[#0A0A0A]', text: 'text-[#0A0A0A]', ring: 'ring-[#0A0A0A]/30' },
  }
  const c = tierColors[tier.level]

  const shareUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const shareText = `I scored "${tier.title}" (${percent}%) on the AI Readiness Quiz. Measure your business AI readiness too.`

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }

  const downloadCard = async () => {
    if (!cardRef.current || downloading) return
    setDownloading(true)
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#FAFAF7',
      })
      const link = document.createElement('a')
      const safeName = (lead?.company || lead?.name || 'ai-readiness')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
      link.download = `${safeName}-ai-readiness-score.png`
      link.href = dataUrl
      link.click()
      setDownloaded(true)
      setTimeout(() => setDownloaded(false), 2500)
    } catch (err) {
      console.error('Failed to generate score card', err)
    } finally {
      setDownloading(false)
    }
  }

  const restart = () => {
    clearAll()
    navigate('/')
  }

  if (!lead) return null

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAF7]">
      <Nav />

      <main className="flex-1">
        {/* Hero result */}
        <section className="relative overflow-hidden border-b border-[#E5E5E5]">
          <div className="absolute inset-0 grid-pattern opacity-50" aria-hidden="true" />
          <div className="absolute -top-40 right-0 w-[500px] h-[500px] bg-[#0F766E]/10 rounded-full blur-3xl" aria-hidden="true" />

          <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="font-mono text-xs uppercase tracking-widest text-[#0F766E] mb-4 fade-up">
              Assessment Complete · {lead.name}
            </div>

            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.05] tracking-tight mb-12 fade-up max-w-4xl" style={{ animationDelay: '0.1s' }}>
              Your AI readiness is<br />
              <span className={`italic ${c.text}`}>"{tier.title}"</span>.
            </h1>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
              {/* Score card */}
              <div className="lg:col-span-5 fade-up" style={{ animationDelay: '0.2s' }}>
                <div className="bg-white rounded-3xl border border-[#E5E5E5] p-8 md:p-10 h-full shadow-xl shadow-[#0A0A0A]/5">
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#0A0A0A]/40 mb-6">
                    Overall Readiness Score
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-display text-7xl md:text-8xl font-semibold tabular-nums">{totalScore}</span>
                    <span className="font-display text-3xl text-[#0A0A0A]/30">/{maxScore}</span>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${c.bg} text-white text-xs font-mono uppercase tracking-widest mb-6`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    {tier.subtitle}
                  </div>

                  {/* Tier scale */}
                  <div className="space-y-2">
                    {[
                      { key: 'emerging', label: 'Emerging', range: '0–34%' },
                      { key: 'developing', label: 'Developing', range: '35–59%' },
                      { key: 'established', label: 'Established', range: '60–84%' },
                      { key: 'leading', label: 'Leading', range: '85–100%' },
                    ].map((t) => {
                      const active = t.key === tier.level
                      return (
                        <div
                          key={t.key}
                          className={`flex items-center justify-between py-2 px-3 rounded-lg text-sm transition ${
                            active ? 'bg-[#0A0A0A] text-white' : 'text-[#0A0A0A]/50'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            {active && <Check size={14} />}
                            {t.label}
                          </span>
                          <span className="font-mono text-xs">{t.range}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="lg:col-span-7 fade-up" style={{ animationDelay: '0.3s' }}>
                <div className="bg-[#0A0A0A] text-[#FAFAF7] rounded-3xl p-8 md:p-10 h-full noise relative overflow-hidden">
                  <div className="font-mono text-[11px] uppercase tracking-widest text-[#F59E0B] mb-6">
                    Diagnosis Summary
                  </div>
                  <p className="font-display text-2xl md:text-3xl leading-snug font-medium mb-8">
                    {tier.description}
                  </p>

                  <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/10">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">Score</div>
                      <div className="font-display text-2xl font-semibold">{percent}<span className="text-base text-white/40">%</span></div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">Tier</div>
                      <div className="font-display text-2xl font-semibold">{tier.title}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">Areas</div>
                      <div className="font-display text-2xl font-semibold">{questions.length}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category breakdown */}
        <section className="bg-white border-b border-[#E5E5E5]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-[#0F766E] mb-3">
                  Category Breakdown
                </div>
                <h2 className="font-display text-3xl md:text-5xl font-medium leading-tight tracking-tight">
                  Scores by area
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
              {categories.map((cat, i) => (
                <div key={cat.category} className="fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="font-display text-lg font-semibold">{cat.category}</span>
                    <span className="font-mono text-sm tabular-nums">
                      <span className="text-[#0F766E] font-semibold">{cat.score}</span>
                      <span className="text-[#0A0A0A]/30">/{cat.max}</span>
                    </span>
                  </div>
                  <div className="h-2 bg-[#F3F3EE] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#0F766E] to-[#0B524C] rounded-full transition-all duration-1000"
                      style={{ width: `${cat.percent}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recommendations */}
        <section className="bg-[#FAFAF7] border-b border-[#E5E5E5]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles size={18} className="text-[#F59E0B]" />
                  <span className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
                    Recommendations
                  </span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-medium leading-tight tracking-tight mb-8">
                  Recommended<br />initiatives
                </h3>
                <ul className="space-y-5">
                  {tier.recommendations.map((rec, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="flex-shrink-0 font-mono text-xs text-[#0F766E] mt-1.5 tabular-nums">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[#0A0A0A]/80 leading-relaxed">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Compass size={18} className="text-[#0F766E]" />
                  <span className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
                    Next Steps
                  </span>
                </div>
                <h3 className="font-display text-3xl md:text-4xl font-medium leading-tight tracking-tight mb-8">
                  Execution roadmap
                </h3>
                <ul className="space-y-5">
                  {tier.nextSteps.map((step, i) => (
                    <li key={i} className="flex gap-4 p-5 bg-white border border-[#E5E5E5] rounded-2xl">
                      <Target size={18} className="flex-shrink-0 text-[#F59E0B] mt-0.5" />
                      <span className="text-[#0A0A0A]/80 leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Share + CTA */}
        <section className="bg-[#0A0A0A] text-[#FAFAF7] noise">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
            {/* Score card preview */}
            <div className="mb-14 fade-up">
              <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-[#F59E0B] mb-3">
                    Shareable Score Card
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-medium leading-tight tracking-tight">
                    Download &amp; share your card
                  </h3>
                  <p className="text-white/60 leading-relaxed mt-2 max-w-xl">
                    A LinkedIn-ready 1200×630 image of your result. Post it, attach it to your CV, or include it in your next investor update.
                  </p>
                </div>
                <button
                  onClick={downloadCard}
                  disabled={downloading}
                  className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#F59E0B] text-[#0A0A0A] rounded-full text-sm font-semibold hover:bg-[#FBBF24] transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {downloading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Generating…
                    </>
                  ) : downloaded ? (
                    <>
                      <Check size={16} />
                      Downloaded!
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Download as PNG
                    </>
                  )}
                </button>
              </div>

              {/* Visual preview (scaled) */}
              <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl">
                <div
                  className="origin-top-left"
                  style={{
                    width: '100%',
                    aspectRatio: '1200 / 630',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      transform: 'scale(var(--card-scale, 0.5))',
                      transformOrigin: 'top left',
                      width: '1200px',
                      height: '630px',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                    }}
                    className="sm:[--card-scale:0.6] md:[--card-scale:0.75] lg:[--card-scale:0.9]"
                  >
                    <ScoreCard
                      name={lead.name}
                      company={lead.company}
                      tierTitle={tier.title}
                      tierLevel={tier.level}
                      percent={percent}
                      totalScore={totalScore}
                      maxScore={maxScore}
                      categories={categories}
                    />
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
                <ImageIcon size={14} />
                <span>1200×630 — perfect for LinkedIn, X, and Open Graph previews.</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-[#F59E0B] mb-4">
                  Share Your Result
                </div>
                <h3 className="font-display text-3xl md:text-5xl font-medium leading-tight tracking-tight mb-4">
                  Share<br />your result
                </h3>
                <p className="text-white/60 leading-relaxed">
                  Share with your team and leadership to start the conversation about AI strategy.
                </p>
              </div>

              <div className="space-y-3">
                <ShareButton
                  icon={Twitter}
                  label="Share on X (Twitter)"
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                />
                <ShareButton
                  icon={Linkedin}
                  label="Share on LinkedIn"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                />
                <ShareButton
                  icon={Facebook}
                  label="Share on Facebook"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                />
                <button
                  onClick={copyLink}
                  className="w-full flex items-center justify-between gap-3 px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all group"
                >
                  <span className="flex items-center gap-3">
                    {copied ? <Check size={18} className="text-[#F59E0B]" /> : <Copy size={18} />}
                    <span className="text-sm font-medium">{copied ? 'Link copied!' : 'Copy link'}</span>
                  </span>
                  <ArrowRight size={16} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition" />
                </button>
              </div>
            </div>

            <div className="mt-16 pt-10 border-t border-white/10 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <p className="text-white/60 text-sm">
                We will also send your results to <span className="text-white">{lead.email}</span>.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={restart}
                  className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 rounded-full text-sm hover:bg-white/5 transition"
                >
                  <RefreshCw size={14} />
                  Retake
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#FAFAF7] text-[#0A0A0A] rounded-full text-sm hover:bg-[#F59E0B] transition"
                >
                  Back to home
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Offscreen render target for high-fidelity download */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: '-10000px',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <ScoreCard
          ref={cardRef}
          name={lead.name}
          company={lead.company}
          tierTitle={tier.title}
          tierLevel={tier.level}
          percent={percent}
          totalScore={totalScore}
          maxScore={maxScore}
          categories={categories}
        />
      </div>
    </div>
  )
}

interface ShareButtonProps {
  icon: React.ComponentType<{ size?: number; className?: string }>
  label: string
  href: string
}

function ShareButton({ icon: Icon, label, href }: ShareButtonProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-full flex items-center justify-between gap-3 px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all group"
    >
      <span className="flex items-center gap-3">
        <Icon size={18} />
        <span className="text-sm font-medium">{label}</span>
      </span>
      <ArrowRight size={16} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition" />
    </a>
  )
}

export default Results
